// 전략과제 현황 — 사업을 전략과제 기준으로 집계해 보는 대시보드
// 사업 선택 카드 첫 칸의 '전체'를 고르면 전체 사업 합계, 사업을 고르면 그 사업을 전략과제 기준으로 보여준다

import { useMemo, useState } from 'react'
import { designTokens } from '@/utils/design-tokens'
import { useAppSelector } from '@/store/hooks'
import { useShowTasks } from '@/hooks/useShowTasks'
import { usePersistedBusinessFilter } from '@/hooks/usePersistedBusinessFilter'
import type { StrategySummaryBusinessItem } from '@/api/generated/models/StrategySummaryBusinessItem'
import type { StrategySummaryDepartmentItem } from '@/api/generated/models/StrategySummaryDepartmentItem'
import MetricTiles from './components/MetricTiles'
import MetricListPanels, {
  type MetricListRequest,
} from './components/MetricListPanels'
import StatusTable, {
  type StatusColumn,
  type StatusRow,
} from './components/StatusTable'
import SummaryCards, { type SummaryCardItem } from './components/SummaryCards'
import { useStrategySummary } from './hooks/useStrategySummary'
import {
  getCurrentSchoolYear,
  getMetricKeys,
  isProgressOnly,
  strategyColors,
  type MetricKey,
} from './strategyMetrics'

/** 사업 선택 카드의 '전체' 카드 id */
const ALL_BUSINESS_ID = '__all__'

/** 목록 패널 제목에 쓰는 항목 이름 */
const LIST_ITEM_NAME: Record<MetricKey, string> = {
  task: '세부실행과제',
  item: '수행업무',
  budget: '예산',
  performance: '성과',
}

const TASK_FOOTNOTE =
  '과제 진행률: 수행업무가 모두 완료된 세부실행과제 수 ÷ 전체 세부실행과제 수'

const FOOTNOTE = {
  department:
    '업무 진행률: 부서가 담당하는 세부실행과제의 완료 수행업무 수 ÷ 전체 수행업무 수 · 예산 집행률: 집행액 합계 ÷ 편성액 합계 · 성과 달성률: 성과를 모두 달성한 세부실행과제 수 ÷ 성과가 있는 세부실행과제 수',
  departmentProgress:
    '업무 진행률: 부서가 담당하는 세부실행과제의 완료 수행업무 수 ÷ 전체 수행업무 수',
  subGroup:
    '업무 진행률: 실행과제에 속한 완료 수행업무 수 ÷ 전체 수행업무 수 · 예산 집행률: 소속 세부실행과제의 집행액 합계 ÷ 편성액 합계 · 성과 달성률: 성과를 모두 달성한 세부실행과제 수 ÷ 성과가 있는 세부실행과제 수',
  subGroupProgress:
    '업무 진행률: 실행과제에 속한 완료 수행업무 수 ÷ 전체 수행업무 수',
}

const toDepartmentRows = (
  departments: StrategySummaryDepartmentItem[] = []
): StatusRow[] =>
  departments.map((d) => ({
    id: d.department_id,
    name: d.department_name,
    metrics: d,
  }))

const countTaskGroups = (businesses: StrategySummaryBusinessItem[]) =>
  businesses.reduce((sum, b) => sum + (b.task_groups?.length ?? 0), 0)

export default function StrategyDashboardPage() {
  const font = designTokens.typography.fontFamily.sans[0]
  const selectedWorkspace = useAppSelector(
    (state) => state.workspace.selectedWorkspace
  )
  // 워크스페이스 '세부실행과제 표시'가 꺼지면 세부실행과제 지표·열·건수를 모두 숨긴다
  const showTasks = useShowTasks()
  const allRates: StatusColumn[] = getMetricKeys(showTasks)
  const progressRates: StatusColumn[] = showTasks ? ['task', 'item'] : ['item']
  const countColumns: StatusColumn[] = showTasks
    ? ['taskCount', 'itemCount']
    : ['itemCount']
  const withTaskNote = (note: string) =>
    showTasks ? `${TASK_FOOTNOTE} · ${note}` : note
  const taskCountText = (count: number | undefined) =>
    showTasks ? ` · 세부실행과제 ${count ?? 0}개` : ''

  const currentSchoolYear = getCurrentSchoolYear()
  const [schoolYear, setSchoolYear] = useState(currentSchoolYear)
  // 고른 사업은 쿠키에 저장돼 다른 메뉴의 사업 필터에도 그대로 걸린다 (빈 값 = 전체)
  const { businessId: savedBusinessId, setBusiness } =
    usePersistedBusinessFilter()
  const [taskGroupId, setTaskGroupId] = useState<string | null>(null)
  // 요약 지표 차트 클릭으로 연 목록 (한 번에 하나)
  const [listRequest, setListRequest] = useState<MetricListRequest | null>(
    null
  )

  const { data, loading, error } = useStrategySummary(
    selectedWorkspace?.id,
    schoolYear
  )
  const businesses = useMemo(() => data?.businesses ?? [], [data])

  // 저장된 사업이 이 화면 목록에 없으면(비활성 사업 등) 전체로 보여준다
  const businessId = businesses.some((b) => b.business_id === savedBusinessId)
    ? savedBusinessId
    : ALL_BUSINESS_ID

  const business = businesses.find((b) => b.business_id === businessId) ?? null
  const taskGroups = business?.task_groups ?? []
  const taskGroupIndex = Math.max(
    0,
    taskGroups.findIndex((g) => g.task_group_id === taskGroupId)
  )
  const taskGroup = taskGroups[taskGroupIndex] ?? null

  const businessCards: SummaryCardItem[] = [
    ...(data
      ? [
          {
            id: ALL_BUSINESS_ID,
            title: '전체',
            metrics: data.total,
            tone: 'dark' as const,
          },
        ]
      : []),
    ...businesses.map((b) => ({
      id: b.business_id,
      title: b.business_name,
      metrics: b,
    })),
  ]

  /**
   * 차트 클릭 → 목록 열기. 사업을 골랐으면 그 사업으로, 기간은 고른 달(없으면 학년도 3월~2월)로 좁힌다.
   * 화면의 지표가 학년도 기준이라 목록도 같은 기간으로 맞춘다.
   */
  const openMetricList = (metricKey: MetricKey, month: string | null) => {
    const [year, monthNumber] = month
      ? month.split('-').map(Number)
      : [schoolYear, 3]
    const startDate = new Date(year, monthNumber - 1, 1)
    const endDate = new Date(year, monthNumber - 1 + (month ? 1 : 12), 1)
    endDate.setMilliseconds(endDate.getMilliseconds() - 1)
    const entity = business ? business.business_name : '전체'
    const period = month
      ? `${year}년 ${monthNumber}월`
      : `${schoolYear}학년도`
    setListRequest({
      metricKey,
      title: `${entity} ${LIST_ITEM_NAME[metricKey]} 목록 (${period})`,
      businessId: business?.business_id,
      businessName: business?.business_name,
      startDate,
      endDate,
    })
  }

  const handleSelectBusiness = (id: string) => {
    if (id === ALL_BUSINESS_ID) {
      setBusiness('', '')
    } else {
      const picked = businesses.find((b) => b.business_id === id)
      setBusiness(id, picked?.business_name ?? '')
    }
    setTaskGroupId(null)
  }

  const textStyle = { fontFamily: font, color: strategyColors.text }
  const subTitleStyle = { fontSize: 13, color: strategyColors.subText }

  const renderMessage = (
    message: string,
    color: string = designTokens.colors.gray[4]
  ) => (
    <div className="p-6 h-full flex items-center justify-center">
      <div
        style={{
          fontFamily: font,
          fontSize: designTokens.typography.fontSize['body-medium'][0],
          color,
        }}
      >
        {message}
      </div>
    </div>
  )

  if (loading && !data) return renderMessage('누적 현황을 불러오는 중...')
  if (error) return renderMessage(error, designTokens.colors.error.DEFAULT)

  const sectionHeader = (title: string, description: string) => (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: 8,
        borderTop: `1px solid ${strategyColors.border}`,
        paddingTop: 20,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{title}</h2>
        <span style={subTitleStyle}>{description}</span>
      </div>
    </div>
  )

  const renderAllView = () => {
    if (!data) return null
    const departmentRows = toDepartmentRows(data.departments)
    return (
      <>
        {sectionHeader(
          '전체 사업 합계',
          `사업 ${businesses.length}개 · 전략과제 ${countTaskGroups(businesses)}개${taskCountText(
            data.total.task_count
          )} · 수행업무 ${data.total.task_item.total_count ?? 0}개`
        )}
        <MetricTiles
          metrics={data.total}
          monthly={data.monthly ?? []}
          schoolYear={schoolYear}
          onOpenList={openMetricList}
        />
        <StatusTable
          title="전체 사업 › 부서별 진행 현황"
          hint={`부서 ${departmentRows.length}개 · 부서명 가나다순 · — 는 해당 없음`}
          firstColumnLabel="부서"
          columns={['itemCount', ...allRates]}
          rows={departmentRows}
          footnote={`모든 사업 합계 · ${withTaskNote(FOOTNOTE.department)}`}
        />
      </>
    )
  }

  const renderIndividualView = () => {
    if (!business) return null
    const progressOnly = isProgressOnly(business)
    const taskGroupCards: SummaryCardItem[] = taskGroups.map((g, index) => ({
      id: g.task_group_id,
      eyebrow: `전략과제 ${index + 1}`,
      title: g.task_group_name,
      footer: `${showTasks ? `세부실행과제 ${g.task_count ?? 0}개 · ` : ''}수행업무 ${g.task_item.total_count ?? 0}개`,
      metrics: g,
    }))
    const titlePrefix = taskGroup
      ? `${taskGroupIndex + 1}. ${taskGroup.task_group_name}`
      : ''
    const subGroupRows: StatusRow[] = (taskGroup?.sub_groups ?? []).map(
      (s) => ({
        id: s.task_sub_group_id,
        name: s.task_sub_group_name,
        metrics: s,
      })
    )
    const departmentRows = toDepartmentRows(taskGroup?.departments)
    const naHint = progressOnly ? '' : ' · — 는 해당 없음'

    return (
      <>
        {sectionHeader(
          business.business_name,
          `전략과제 ${taskGroups.length}개${taskCountText(business.task_count)} · 수행업무 ${
            business.task_item.total_count ?? 0
          }개`
        )}
        <MetricTiles
          metrics={business}
          monthly={business.monthly ?? []}
          schoolYear={schoolYear}
          onOpenList={openMetricList}
        />

        <section
          aria-label="전략과제별 성과"
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <h2 style={{ margin: 0, fontSize: 21, fontWeight: 700 }}>
            전략과제별 성과
          </h2>
          {taskGroupCards.length === 0 ? (
            <span style={subTitleStyle}>등록된 전략과제가 없습니다.</span>
          ) : (
            <SummaryCards
              variant="task-group"
              items={taskGroupCards}
              selectedId={taskGroup?.task_group_id ?? null}
              onSelect={setTaskGroupId}
            />
          )}
        </section>

        {taskGroup && (
          <>
            <StatusTable
              title={`${titlePrefix} › ${showTasks ? '실행과제·세부실행과제별' : '실행과제별'} 현황`}
              hint={`실행과제 ${subGroupRows.length}개 · 등록 순${naHint}`}
              firstColumnLabel="실행과제"
              columns={[
                ...countColumns,
                ...(progressOnly ? progressRates : allRates),
              ]}
              rows={subGroupRows}
              footnote={withTaskNote(
                progressOnly ? FOOTNOTE.subGroupProgress : FOOTNOTE.subGroup
              )}
            />
            <StatusTable
              title={`${titlePrefix} › 부서별 진행 현황`}
              hint={`부서 ${departmentRows.length}개 · 부서명 가나다순${naHint}`}
              firstColumnLabel="부서"
              columns={
                progressOnly
                  ? [...countColumns, ...progressRates]
                  : ['itemCount', ...allRates]
              }
              rows={departmentRows}
              footnote={withTaskNote(
                progressOnly ? FOOTNOTE.departmentProgress : FOOTNOTE.department
              )}
            />
          </>
        )}
      </>
    )
  }

  return (
    <div className="p-6 h-full flex flex-col" style={textStyle}>
      <div className="flex-1 overflow-y-auto">
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 24,
            paddingBottom: 24,
          }}
        >
          {businesses.length === 0 ? (
            <div
              style={{
                padding: '48px 0',
                textAlign: 'center',
                ...subTitleStyle,
                fontSize: 16,
              }}
            >
              표시할 사업이 없습니다.
            </div>
          ) : (
            <>
              <section
                aria-label="사업 선택"
                style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <h2 style={{ margin: 0, fontSize: 21, fontWeight: 700 }}>
                    사업 선택
                  </h2>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 16,
                    }}
                  >
                    <span style={subTitleStyle}>
                      전체 {businesses.length}개 사업 · 카드를 누르면 해당
                      사업을 볼 수 있습니다 · — 는 해당 없음
                    </span>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 14,
                      }}
                    >
                      <span style={{ color: strategyColors.subText }}>
                        학년도
                      </span>
                      <select
                        value={schoolYear}
                        onChange={(e) => setSchoolYear(Number(e.target.value))}
                        style={{
                          height: 36,
                          padding: '0 8px',
                          border: `1px solid ${strategyColors.dashed}`,
                          borderRadius: 4,
                          fontFamily: font,
                          fontSize: 14,
                          color: strategyColors.text,
                          background: strategyColors.surface,
                        }}
                      >
                        {[0, 1, 2].map((offset) => {
                          const year = currentSchoolYear - offset
                          return (
                            <option key={year} value={year}>
                              {year}학년도 ({year}.3 ~ {year + 1}.2)
                            </option>
                          )
                        })}
                      </select>
                    </label>
                  </div>
                </div>
                <SummaryCards
                  variant="business"
                  items={businessCards}
                  selectedId={businessId}
                  onSelect={handleSelectBusiness}
                />
              </section>

              {businessId === ALL_BUSINESS_ID
                ? renderAllView()
                : renderIndividualView()}
            </>
          )}
        </div>
      </div>
      <MetricListPanels
        workspaceId={selectedWorkspace?.id ?? ''}
        request={listRequest}
        onClose={() => setListRequest(null)}
      />
    </div>
  )
}
